import { ALBUM_GET_MULTIMEDIAS, ALBUM_VOTE_MULTIMEDIA } from 'constants/api'
import { useCallback, useEffect, useState } from 'react'
import axios from 'axios'
import { useSession } from 'contexts/session-context'
import { F1_CAMPAIGN_CLOSED, PIT_STOP_IDS } from '../constants'
import {
  F1GalleryPhoto,
  F1_VOTED_STORAGE_KEY,
  F1_VOTE_ERROR,
  applyVote,
  markVoted,
  normalizePhotos,
  readVotedIds,
  selectVisiblePhotos,
  sortByVotes
} from '../gallery'

export type F1VoteResult = 'ok' | 'alreadyVoted' | 'maxVotes' | 'error'

// Build-time API token (craco define) required by the listing endpoint.
// Empty/undefined means the gallery cannot load at all.
const apiToken = typeof API_TOKEN !== 'undefined' && API_TOKEN ? API_TOKEN : ''

// Gallery data for the Pit Stop 4 landing sections (no AlbumProvider).
// Single source for visitors and logged users (contract verified 2026-10-20):
// GET rally/getmultimedias?id=<rallyId>&apiToken=<API_TOKEN> returns the
// rally envelope whose `multimedias[].votes` is the REAL total per photo.
// The gallery keeps backend order; the ranking is the same list sorted by
// votes. Which photos the user already voted comes only from localStorage.
export const usePitStopGallery = (enabled = true) => {
  const [{ data: session }] = useSession()
  const [photos, setPhotos] = useState<F1GalleryPhoto[]>([])
  const [ranking, setRanking] = useState<F1GalleryPhoto[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<boolean>(false)
  const [votedIds, setVotedIds] = useState<number[]>(() => readVotedIds())

  const idToken = ((session as any)?._tokenResponse as any)?.idToken

  const fetchGallery = useCallback(async (silent = false) => {
    if (PIT_STOP_IDS.photoAlbumId === null) {
      setLoading(false)
      setError(true)
      return
    }
    if (!apiToken) {
      console.error(
        'usePitStopGallery: API_TOKEN is not defined at build time; the gallery listing (rally/getmultimedias) cannot be requested'
      )
      setPhotos([])
      setRanking([])
      setLoading(false)
      setError(true)
      return
    }
    if (!silent) setLoading(true)

    try {
      const { data } = await axios.get(
        `${ALBUM_GET_MULTIMEDIAS}?id=${PIT_STOP_IDS.photoAlbumId}&apiToken=${apiToken}`,
        { headers: { 'Ph-Api-Id': `${PH_API_ID ?? 1}` } }
      )
      const nextPhotos = selectVisiblePhotos(normalizePhotos(data?.multimedias))
      setPhotos(nextPhotos)
      setRanking(sortByVotes(nextPhotos))
      setError(false)
    } catch (requestError) {
      // Failed listing: both visitors and logged users get the "no gallery" mode
      setPhotos([])
      setRanking([])
      setError(true)
    }

    if (!silent) setLoading(false)
  }, [])

  useEffect(() => {
    if (!enabled) {
      setLoading(false)
      return
    }
    fetchGallery()
  }, [enabled, fetchGallery])

  const vote = useCallback(
    async (photo: F1GalleryPhoto): Promise<F1VoteResult> => {
      // Campaign closed: the UI never offers the vote action, and this guard
      // keeps a stale caller from posting a vote anyway
      if (F1_CAMPAIGN_CLOSED) return 'error'
      // Optimistic on the real totals: corrected on failure
      setPhotos((prev) => applyVote(prev, photo.id))
      setRanking((prev) => applyVote(prev, photo.id))
      setVotedIds(markVoted(photo.id))
      try {
        await axios.post(
          `${ALBUM_VOTE_MULTIMEDIA}?rallyMultimediaId=${photo.id}`,
          {},
          {
            headers: {
              'Ph-Api-Id': `${PH_API_ID ?? 1}`,
              ...(idToken ? { Authorization: `Bearer ${idToken}` } : {})
            }
          }
        )
        // Silent refetch re-reads the listing so totals stay in sync
        fetchGallery(true)
        return 'ok'
      } catch (requestError) {
        const code = (requestError as any)?.response?.data?.code
        const revert = (items: F1GalleryPhoto[]) =>
          items.map((item) => (item.id === photo.id ? { ...item, votes: photo.votes } : item))
        setPhotos(revert)
        setRanking(revert)
        if (code === F1_VOTE_ERROR.alreadyVoted) {
          // The backend already counted this user: keep it marked
          return 'alreadyVoted'
        }
        setVotedIds((prev) => prev.filter((id) => id !== photo.id))
        try {
          window.localStorage.setItem(
            F1_VOTED_STORAGE_KEY,
            JSON.stringify(readVotedIds().filter((id) => id !== photo.id))
          )
        } catch (storageError) {
          // Storage unavailable: in-memory state already corrected
        }
        if (code === F1_VOTE_ERROR.maxVotes) return 'maxVotes'
        return 'error'
      }
    },
    [fetchGallery, idToken]
  )

  return { photos, ranking, loading, error, votedIds, fetchGallery, vote }
}

export default usePitStopGallery
